import React from 'react';

import { formatMoney, toMoney } from '@betterment/core';
import { Banner, Box, Button, Card, H2, ListRow, Separator, Text } from '@betterment/samba-react';
import { useFeatureFlag } from '@betterment/shared-components';
import { useFormikContext } from 'formik';

import { PlatformFeePaidBy } from '../../../../graphql/schema';
import { parseFloatFromNumberInputMask } from '../../../../utils/input-helpers';
import { BillingRateType, FeeSummaryBoxProps, getBillingRateType } from '../data';

const FeeSummaryBox: React.FC<FeeSummaryBoxProps> = ({ billingPlan, platformFee, numberOfClients }) => {
  const { isSubmitting } = useFormikContext();

  const { enabled: isCliffBasedBillingEnabled, loading: isCliffBasedBillingLoading } = useFeatureFlag({
    splitName: 'retail.b4a_cliff_based_billing_q1_2026_enabled',
    context: 'BillingPlanFlow',
  });

  const shouldShowCliffBasedBillingChanges = isCliffBasedBillingEnabled && !isCliffBasedBillingLoading;

  const getButtonText = () => {
    if (billingPlan.id) {
      // @ts-expect-error TS18048 violates strict null checks
      return numberOfClients > 0 ? 'Continue' : 'Update plan';
    }
    return 'Create plan';
  };

  const {
    values: { platformFeePaidBy, sharedPlatformFeeClientPortionBps = '0' },
  } = useFormikContext<{
    platformFeePaidBy: PlatformFeePaidBy;
    sharedPlatformFeeClientPortionBps: string;
  }>();

  const getFirmRate = () => {
    if (platformFeePaidBy === PlatformFeePaidBy.ClientOnly) {
      return 0;
    }
    if (platformFeePaidBy === PlatformFeePaidBy.AdvisorOnly) {
      return platformFee;
    }
    const parsedPlatformFeeClientPortionBps = parseFloatFromNumberInputMask(sharedPlatformFeeClientPortionBps) || 0;
    return platformFee - parsedPlatformFeeClientPortionBps;
  };

  const getClientPortion = () => {
    return platformFee - getFirmRate();
  };

  const { billingRate } = billingPlan;

  const shouldShowInfoBanner = () =>
    shouldShowCliffBasedBillingChanges &&
    [BillingRateType.Tiered, BillingRateType.AssetBased].includes(getBillingRateType(billingRate));

  const displayClientRate = () => {
    switch (getBillingRateType(billingRate)) {
      case BillingRateType.AssetBased: {
        // @ts-expect-error TS18048 violates strict null checks
        const feeRate = Number.isNaN(billingRate.assetBasedConfiguration.feeRate)
          ? 0
          : // @ts-expect-error TS18048 violates strict null checks
            billingRate.assetBasedConfiguration.feeRate;
        return `${feeRate + getClientPortion()} bps`;
      }
      case BillingRateType.Fixed:
        // @ts-expect-error TS18048 violates strict null checks
        return `${formatMoney(billingRate.fixedConfiguration.feeAmount ?? toMoney(0))} + ${getClientPortion()} bps`;
      case BillingRateType.Tiered:
        return `Tiered advisory fee + ${getClientPortion()} bps`;
      default:
        return 'N/A';
    }
  };

  const displayFirmRate = () => {
    return `${getFirmRate()} bps`;
  };

  const shouldShowClientPortionOfPlatformFee = () => {
    return getBillingRateType(billingRate) === BillingRateType.AssetBased && getClientPortion() > 0;
  };

  return (
    <Card
      css={{
        width: 'container.medium',
        paddingInline: 'space.300',
        borderStyle: 'solid',
        borderWidth: 'thin',
        borderColor: 'border',
        boxShadow: 'none',
      }}
    >
      <Card.Header
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          py: 'space.300',
        }}
      >
        <H2 variant="title200">Summary</H2>
      </Card.Header>
      <Separator />
      <Card.Content>
        <ListRow data-testid="client-rate" css={{ px: '0px' }}>
          <ListRow.LeadingContent>
            <Text>Client pays</Text>
          </ListRow.LeadingContent>
          <ListRow.TrailingContent>
            <Text>{displayClientRate()}</Text>
            {shouldShowClientPortionOfPlatformFee() && (
              <Text color="text.weak" variant="text50">
                Includes {getClientPortion()} bps platform fee
              </Text>
            )}
          </ListRow.TrailingContent>
        </ListRow>
        <Box>
          <Separator />
          <ListRow data-testid="firm-rate" css={{ px: '0px' }}>
            <ListRow.LeadingContent>
              <Text>Your firm pays</Text>
            </ListRow.LeadingContent>
            <ListRow.TrailingContent>
              <Text>{displayFirmRate()}</Text>
            </ListRow.TrailingContent>
          </ListRow>
        </Box>
      </Card.Content>
      <Separator />
      <Card.Footer
        css={{
          py: 'space.300',
        }}
      >
        {shouldShowInfoBanner() && (
          <Banner variant="info" css={{ mb: 'space.200' }}>
            <Banner.Content>
              <Text>
                Fees accrue daily. Market changes may impact clients’ billable balances and result in tier adjustments.
              </Text>
            </Banner.Content>
          </Banner>
        )}
        <Button type="submit" disabled={isSubmitting} css={{ width: '100%' }}>
          {getButtonText()}
        </Button>
      </Card.Footer>
    </Card>
  );
};

export default FeeSummaryBox;
